/** Shared, locale-stable formatters for the admin metrics surface. */

const INT = new Intl.NumberFormat('en-US');
const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/** Grouped integer, e.g. 1,234,567. */
export const fmtInt = (n: number): string => INT.format(Math.round(n));

/** A 0–1 share as a percentage: one decimal under 10% (where it matters), none above. */
export const fmtPct = (share: number): string => {
  const pct = share * 100;
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
};

/** Compact integer for tight tiles, e.g. 1.2M. */
export const fmtCompact = (n: number): string => COMPACT.format(n);

/** USD, with extra precision under a dollar so fractions of a cent still read. */
export const fmtUsd = (n: number): string => {
  if (n === 0) return '$0.00';
  if (n < 1) return `$${n.toFixed(4)}`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Absolute timestamp, e.g. "14 Jun 2026, 21:43". */
export const fmtDateTime = (d: string | Date): string =>
  new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Coarse relative time, e.g. "3m ago", "2d ago", "just now". */
export const relativeTime = (d: string | Date | null | undefined): string => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const s = Math.round(ms / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};
